import qs from "query-string";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { encodeQueryParams, StringParam } from "serialize-query-params";
import { createEnumParam } from "use-query-params";

import {
  CARD_BOARD_URL_PATH,
  editFormDataAF,
  generateSpecificSearchPattern,
  getRideAddUrl,
  GRID_MULTI_SELECT_URL_PATH,
  hookFormContextAF,
  IRideDto,
  isGridSingleSelectViewPathname,
  isRidesAddRoutePathname,
  isRidesEditRoutePathname,
  KANBAN_URL_PATH,
  MAP_URL_PATH,
  markKeepEditFormDtoOnUnmount,
  RequestBodyModel,
  resolveRideEditBasePath,
  RIDE_NOT_REVERSED_PARAM_VALUE,
  RIDE_REVERSE_SUFFIX,
  RIDE_REVERSED_PARAM_VALUE,
  RideCrudModeKey,
  RideCrudModeKeyTuple,
  rideEditNavigationBaseByType,
  RideRoutes,
  RIDES_CALENDAR_URL_PATH,
  RIDES_URL,
  ridesIdSearchRefetchTokenAtom,
  RidesViewTypeEnum,
  saveLastNavigation,
  starredDisplayModeAtomFamily,
  StarredDisplayModeEnum,
  tableSearchAF,
  TOP_CALENDAR_DATE1_QUERY_NAME,
  TOP_CALENDAR_DATE2_QUERY_NAME,
  TOP_CALENDAR_MODE_QUERY_NAME,
  topCalendarDatesAtomFamily,
  TopCalendarMode,
  usePrefixedRouting,
} from "@ryde/shared";

import {
  useNewRideTemplate,
  usePreGlobalSearchCalendar,
  useRideFormCleanup,
  useTopCalendar,
} from "../hooks";
import { INavigateRideAddParams } from "./use-rides-navigation.models";

interface IRideNavigateArguments {
  rideIds: number[];
  date2: TDateISO;
  date1: TDateISO;
}

export const useRidesNavigation = () => {
  const location = useLocation();
  const { getNewRideTemplate } = useNewRideTemplate();
  const setRideDto = useSetRecoilState<Partial<IRideDto> | undefined>(
    editFormDataAF(RideCrudModeKeyTuple),
  );
  const setRideTopCalendarDates = useSetRecoilState(
    topCalendarDatesAtomFamily(RideCrudModeKey.uiUnit),
  );
  const rideFormContext = useRecoilValue(
    hookFormContextAF(RideCrudModeKey.uiUnit),
  );
  const { reInitTopCalendarUnitFromLocationQueryString } = useTopCalendar(
    RideCrudModeKey.uiUnit,
  );
  const { saveSnapshotIfNeeded } = usePreGlobalSearchCalendar();

  const { fullResetPreviousRideEditData, cleanupRideFormStateAtoms } =
    useRideFormCleanup();
  const { navigatePrefixed, addPrefix } = usePrefixedRouting();
  const setTableSearch = useSetRecoilState(tableSearchAF("ride"));
  const setStarredDisplayMode = useSetRecoilState(
    starredDisplayModeAtomFamily("ride"),
  );
  const setIdSearchRefetchToken = useSetRecoilState(
    ridesIdSearchRefetchTokenAtom,
  );

  const isGridMultiSelectView = useMemo(
    (): boolean => location.pathname.includes(GRID_MULTI_SELECT_URL_PATH),
    [location.pathname],
  );

  const isGridSingleSelectView = useMemo(
    (): boolean => isGridSingleSelectViewPathname(location.pathname),
    [location.pathname],
  );

  const isKanbanView = useMemo(
    (): boolean => location.pathname.includes(KANBAN_URL_PATH),
    [location.pathname],
  );
  const isCardBoardView = useMemo(
    (): boolean => location.pathname.includes(CARD_BOARD_URL_PATH),
    [location.pathname],
  );

  const isRidesMapView = useMemo(
    (): boolean => location.pathname.includes(MAP_URL_PATH),
    [location.pathname],
  );

  const isRidesCalendarView = useMemo(
    (): boolean =>
      location.pathname.includes(`${RIDES_URL}${RIDES_CALENDAR_URL_PATH}`),
    [location.pathname],
  );

  const isRidesRoute = useMemo(
    (): boolean =>
      location.pathname.includes(RIDES_URL) ||
      location.pathname.includes(MAP_URL_PATH),
    [location.pathname],
  );

  const isRidesEditRoute = useMemo(
    (): boolean => isRidesEditRoutePathname(location.pathname),
    [location.pathname],
  );

  const isRidesAddRoute = useMemo(
    (): boolean => isRidesAddRoutePathname(location.pathname),
    [location.pathname],
  );

  const isRidesEditOrAddRoute = useMemo((): boolean => {
    return isRidesAddRoute || isRidesEditRoute;
  }, [isRidesAddRoute, isRidesEditRoute]);

  const ridesPageViewType = useMemo((): RidesViewTypeEnum | undefined => {
    switch (true) {
      case isCardBoardView:
        return RidesViewTypeEnum.CardBoard;
      case isKanbanView:
        return RidesViewTypeEnum.Kanban;
      case isRidesCalendarView:
        return RidesViewTypeEnum.Calendar;
      case isGridMultiSelectView:
        return RidesViewTypeEnum.GridMultiSelect;
      case isGridSingleSelectView:
        return RidesViewTypeEnum.GridSingleSelect;
      case isRidesMapView:
        return RidesViewTypeEnum.Map;
      default:
        return undefined;
    }
  }, [
    isCardBoardView,
    isKanbanView,
    isRidesCalendarView,
    isGridMultiSelectView,
    isGridSingleSelectView,
    isRidesMapView,
  ]);

  const navigateRidesAdd = useEvent(
    async ({
      keepQuery = true,
      initialData,
      rideIdCopyFrom,
      revertDirection,
    }: INavigateRideAddParams): Promise<void> => {
      if (initialData || rideIdCopyFrom) {
        markKeepEditFormDtoOnUnmount(RideCrudModeKeyTuple);
      }

      if (!initialData) {
        fullResetPreviousRideEditData();
      } else {
        cleanupRideFormStateAtoms();
      }

      if (initialData) {
        rideFormContext?.reset(initialData);
        setRideDto(initialData);
      } else if (!rideIdCopyFrom) {
        setRideDto(await getNewRideTemplate());
      }

      let queryString = isRidesRoute && keepQuery ? location.search || "" : "";

      let duplicateRouteSuffix = "";

      if (rideIdCopyFrom && +rideIdCopyFrom) {
        duplicateRouteSuffix = `/${rideIdCopyFrom}`;

        const reverseSuffixComputed = revertDirection
          ? RIDE_REVERSED_PARAM_VALUE
          : RIDE_NOT_REVERSED_PARAM_VALUE;
        const searchParams = new URLSearchParams(queryString);
        searchParams.set(RIDE_REVERSE_SUFFIX, reverseSuffixComputed);
        queryString = `?${searchParams.toString()}`;
      }

      const addRideUrlToNavigate = getRideAddUrl(
        ridesPageViewType,
        duplicateRouteSuffix,
        queryString,
      );
      navigatePrefixed(addRideUrlToNavigate);
    },
  );

  const getEditBasePath = (): string =>
    resolveRideEditBasePath(location.pathname);

  const navigateRidesEdit = useEvent(
    ({
      id,
      keepQuery = true,
      skipFormReset = false,
      initialData,
    }: {
      id: number;
      keepQuery?: boolean;
      skipFormReset?: boolean;
      initialData?: RequestBodyModel;
    }): void => {
      const queryString =
        isRidesRoute && keepQuery ? location.search || "" : "";

      // Keep cached dto when switching rides — stale dto is hidden by the
      // drawer loader until fetch completes. Skip flag cleanup on post-save
      // navigation: processApiResponse already cleaned, then re-armed
      // ridePostSaveTransition. KEEP so unmount does not wipe dto mid-switch.
      markKeepEditFormDtoOnUnmount(RideCrudModeKeyTuple);
      if (!skipFormReset && !initialData) {
        cleanupRideFormStateAtoms();
      }

      if (initialData) {
        rideFormContext?.reset(initialData);
        setRideDto(initialData);
      }

      const editBasePath = getEditBasePath();
      navigatePrefixed(`${editBasePath}${id}${queryString}`);
    },
  );

  const navigateRidesGridMultiSelect = useEvent((keepQuery = true): void => {
    const queryString = isRidesRoute && keepQuery ? location.search || "" : "";
    navigatePrefixed(RideRoutes.SEARCH_GRID_MULTI_SELECT + queryString);
    saveLastNavigation({
      route: RIDES_URL,
      path: RideRoutes.SEARCH_GRID_MULTI_SELECT,
    });
  });

  const navigateRidesGridSingleSelect = useEvent((keepQuery = true): void => {
    const queryString = isRidesRoute && keepQuery ? location.search || "" : "";
    navigatePrefixed(RideRoutes.SEARCH_GRID_SINGLE_SELECT + queryString);
    saveLastNavigation({
      route: RIDES_URL,
      path: RideRoutes.SEARCH_GRID_MULTI_SELECT,
    });
  });

  const rideSearchGrid = isRidesRoute ? (location.search ?? "") : "";

  const navigateRidesKanban = useEvent((keepQuery = true): void => {
    const queryString = isRidesRoute && keepQuery ? location.search || "" : "";
    navigatePrefixed(RideRoutes.SEARCH_KANBAN + queryString);
    saveLastNavigation({ route: RIDES_URL, path: RideRoutes.SEARCH_KANBAN });
  });
  const navigateRidesCardBoard = useEvent((keepQuery = true): void => {
    const queryString = isRidesRoute && keepQuery ? location.search || "" : "";
    navigatePrefixed(RideRoutes.SEARCH_CARD_BOARD + queryString);
    saveLastNavigation({
      route: RIDES_URL,
      path: RideRoutes.SEARCH_CARD_BOARD,
    });
  });

  const navigateRidesMap = useEvent((keepQuery = true): void => {
    const queryString = isRidesRoute && keepQuery ? location.search || "" : "";
    navigatePrefixed(RideRoutes.SEARCH_MAP + queryString);
  });

  const navigateRidesCalendar = useEvent((keepQuery = true): void => {
    const queryString = isRidesRoute && keepQuery ? location.search || "" : "";
    navigatePrefixed(RideRoutes.SEARCH_CALENDAR + queryString);
    saveLastNavigation({
      route: RIDES_URL,
      path: RideRoutes.SEARCH_CALENDAR,
    });
  });

  const navigateRidesGridOrKanbanOrMap = useEvent((keepQuery = true): void => {
    if (isCardBoardView) {
      navigateRidesCardBoard(keepQuery);
    } else if (isKanbanView) {
      navigateRidesKanban(keepQuery);
    } else if (isRidesMapView) {
      navigateRidesMap(keepQuery);
    } else if (isRidesCalendarView) {
      navigateRidesCalendar(keepQuery);
    } else if (isGridSingleSelectView) {
      navigateRidesGridSingleSelect(keepQuery);
    } else {
      navigateRidesGridMultiSelect(keepQuery);
    }
  });

  const getRideEditLink = ({
    id,
    viewType,
    keepQuery = false,
  }: {
    id: number;
    viewType?: RidesViewTypeEnum;
    keepQuery?: boolean;
  }): string => {
    const viewTypeComputed =
      viewType || ridesPageViewType || RidesViewTypeEnum.GridMultiSelect;
    const queryString = isRidesRoute && keepQuery ? location.search || "" : "";
    const base = rideEditNavigationBaseByType[viewTypeComputed];
    return addPrefix(`${base}${id}${queryString}`);
  };

  const getRideSetDatesAndIdsFiltersLink = useEvent(
    ({ rideIds, date1, date2 }: IRideNavigateArguments): string => {
      const encodedQuery = encodeQueryParams(
        {
          [TOP_CALENDAR_DATE1_QUERY_NAME]: StringParam,
          [TOP_CALENDAR_DATE2_QUERY_NAME]: StringParam,
          [TOP_CALENDAR_MODE_QUERY_NAME]: createEnumParam<TopCalendarMode>(
            Object.values(TopCalendarMode),
          ),
        },
        {
          [TOP_CALENDAR_DATE1_QUERY_NAME]: date1,
          [TOP_CALENDAR_DATE2_QUERY_NAME]: date2,
          [TOP_CALENDAR_MODE_QUERY_NAME]: TopCalendarMode.CUSTOM,
        },
      );

      const searchPattern = generateSpecificSearchPattern(rideIds);
      const queryString = qs.stringify({
        ...encodedQuery,
        search: searchPattern,
      });

      return addPrefix(`${RideRoutes.SEARCH_GRID_MULTI_SELECT}?${queryString}`);
    },
  );

  const getRideEditLinkWithFilters = useEvent(
    ({
      rideId,
      date1,
      date2,
    }: {
      rideId: number;
      date1: TDateISO;
      date2: TDateISO;
    }): string => {
      const editBasePath = getEditBasePath();
      const encodedQuery = encodeQueryParams(
        {
          [TOP_CALENDAR_DATE1_QUERY_NAME]: StringParam,
          [TOP_CALENDAR_DATE2_QUERY_NAME]: StringParam,
          [TOP_CALENDAR_MODE_QUERY_NAME]: createEnumParam<TopCalendarMode>(
            Object.values(TopCalendarMode),
          ),
        },
        {
          [TOP_CALENDAR_DATE1_QUERY_NAME]: date1,
          [TOP_CALENDAR_DATE2_QUERY_NAME]: date2,
          [TOP_CALENDAR_MODE_QUERY_NAME]: TopCalendarMode.CUSTOM,
        },
      );

      const searchPattern = generateSpecificSearchPattern([rideId]);
      const queryString = qs.stringify({
        ...encodedQuery,
        search: searchPattern,
      });

      return addPrefix(`${editBasePath}${rideId}?${queryString}`);
    },
  );

  const navigateToRideSetDatesAndIdsFilters = useEvent(
    ({ rideIds, date1, date2 }: IRideNavigateArguments): void => {
      setStarredDisplayMode(StarredDisplayModeEnum.ShowAllIncludeStarred);
      reInitTopCalendarUnitFromLocationQueryString();
      setTableSearch(generateSpecificSearchPattern(rideIds));
      setIdSearchRefetchToken(token => token + 1);
      const link = getRideSetDatesAndIdsFiltersLink({ rideIds, date1, date2 });
      navigatePrefixed(link);
    },
  );

  const globalSearchRideNavigate = useEvent(
    ({ rideIds, date1, date2 }: IRideNavigateArguments): void => {
      // Snapshot current calendar before overriding with the found ride date span.
      saveSnapshotIfNeeded();
      setStarredDisplayMode(StarredDisplayModeEnum.ShowAllIncludeStarred);
      navigatePrefixed(`${RideRoutes.SEARCH_GRID_MULTI_SELECT}`);
      setTableSearch(generateSpecificSearchPattern(rideIds));
      setRideTopCalendarDates({
        date1,
        date2,
      });
      setIdSearchRefetchToken(token => token + 1);
    },
  );

  return {
    isGridMultiSelectView,
    isGridSingleSelectView,
    isKanbanView,
    isCardBoardView,
    isRidesMapView,
    isRidesCalendarView,
    isRidesAddRoute,
    isRidesEditRoute,
    isRidesEditOrAddRoute,
    isRidesRoute,
    ridesPageViewType,
    navigateRidesGridMultiSelect,
    navigateRidesGridSingleSelect,
    navigateRidesKanban,
    navigateRidesCardBoard,
    navigateRidesMap,
    navigateRidesCalendar,
    navigateRidesGridOrKanbanOrMap,
    navigateRidesAdd,
    navigateRidesEdit,
    rideSearchGrid,
    getRideEditLink,
    getRideEditLinkWithFilters,
    getRideSetDatesAndIdsFiltersLink,
    navigateToRideSetDatesAndIdsFilters,
    getEditBasePath,
    globalSearchRideNavigate,
  };
};
